import L from "leaflet";
import satelliteGeoCalc from "./OwnGeoConversion";
import {pathTypeHeadland, pathTypeHeadlandExtension, pathTypeWatering, pathTypeWateringExtension} from "../components/rainrover/RainRoverConst";


const TAG = "OwnMapMath";

export function getLatLngBounds(latLngPoints) {
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    latLngPoints.forEach(p => {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
    });

    return new L.LatLngBounds(new L.LatLng(minLat, minLng), new L.LatLng(maxLat, maxLng));
}



export function getAverageLatLng(latLngPoints) {
    let avgLat = 0;
    let avgLng = 0;
    let avgAlt = 0;
    let isAltDefined = false;

    if (latLngPoints) {
        latLngPoints.forEach(p => {
            avgLat += p.lat;
            avgLng += p.lng;
            if (p.alt) {
                isAltDefined = true;
                avgAlt += p.alt;
            }
        });

        avgLat /= latLngPoints.length;
        avgLng /= latLngPoints.length;
        avgAlt /= latLngPoints.length;

        return new L.LatLng(avgLat, avgLng, (isAltDefined ? avgAlt : undefined));
    } else {
        return undefined;
    }
}



export function getLocalCoordinates(originLatLng, positionsLatLng) {
    return positionsLatLng.map(p => satelliteGeoCalc.lla_to_enu(p, originLatLng));
}

export function getGlobalCoordinates(originLatLng, path) {
    return path.map(p => satelliteGeoCalc.enu_to_lla(p, originLatLng));
}



export function getAverageZ(points) {
    return points.map(p => p.z).reduce((z1, z2) => 0.5 * (z1 + z2));
}


export function convertToWorkSpace(points, scale) {
    return points.map(p => ({X: p.x * scale, Y: p.y * scale}));
}

export function convertFromWorkSpace(points, z, scale) {
    return points.map(p => ({x: p.X / scale, y: p.Y / scale, z: z}));
}




export function sortPoints(points) {
    if (points.length <= 1) return points;
    // console.log(TAG, "sorting points", points);

    let array = [...points];

    const getVector2d = (p1, p2) => ({x: p2.x - p1.x, y: p2.y - p1.y});
    // const getDistance2d = (p1, p2) => Math.sqrt((p2.x - p1.x)**2 + (p2.y - p1.y)**2);
    const getDot2d = (p1, p2) => (p1.x * p2.x + p1.y * p2.y);

    // const getCost = (arr) => {
    //     let cost = 0;
    //     for (let i = 0; i < arr.length - 1; i++) {
    //         cost += getDistance2d(arr[i], arr[i + 1]);
    //     }
    //     return cost;
    // };

    // console.log(TAG, "length", array.length, "cost", getCost(array));

    let count = 0;
    for (let k = 1; k < array.length; ++k) {
        for (let i = 1; i < array.length - 1 && count < 100; ++i, ++count) {
            let v01 = getVector2d(array[i-1], array[i]);
            let v12 = getVector2d(array[i], array[i+1]);
            let dot = getDot2d(v01, v12);
            // console.log(TAG, "i", i, "v01", v01, "v12", v12, "dot", dot);
            if (dot < 0) {
                // console.log(TAG, "swapping", i, i-1);
                let swap = array[i];
                array[i] = array[i-1];
                array[i-1] = swap;
            }
            // console.log(TAG, "i", i, "count", count, "cost", getCost(array));
        }
    }
    // console.log(TAG, "sorted points", array);
    return array;
}


export function extendPathXY(points, extensionLength) {
    if (points.length < 2) return points;

    let array = [...points];
    extendPathStartXY(array, extensionLength);
    extendPathEndXY(array, extensionLength);

    return array;
}



export function extendPathStartXY(points, extensionLength) {
    points.unshift(calculateExtensionStartXY(points, extensionLength));
}

export function calculateExtensionStartXY(points, extensionLength) {
    let dx1 = points[0].X - points[1].X;
    let dy1 = points[0].Y - points[1].Y;
    let n1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    dx1 /= n1;
    dy1 /= n1;
    return {X: points[0].X + extensionLength * dx1, Y: points[0].Y + extensionLength * dy1};
}


export function extendPathEndXY(points, extensionLength) {
    points.push(calculateExtensionEndXY(points, extensionLength));
}

export function calculateExtensionEndXY(points, extensionLength) {
    let lastIdx = points.length - 1;
    let dx2 = points[lastIdx].X - points[lastIdx - 1].X;
    let dy2 = points[lastIdx].Y - points[lastIdx - 1].Y;
    let n2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    dx2 /= n2;
    dy2 /= n2;
    return {X: points[lastIdx].X + extensionLength * dx2, Y: points[lastIdx].Y + extensionLength * dy2};
}



export function getMaxDistanceXY(points) {
    let dist = 0;
    for (let i = 0; i < points.length - 1; ++i) {
        for (let j = i + 1; j < points.length; ++j) {
            let dx = points[j].X - points[i].X;
            let dy = points[j].Y - points[i].Y;
            dist = Math.max(dist, Math.sqrt(dx**2 + dy**2));
        }
    }
    return dist;
}



export function orderPointsXY(points) {

    if (points.length <= 2) return points;

    const avgPos = {
        X: points.reduce((sum, p) => sum + p.X, 0) / points.length,
        Y: points.reduce((sum, p) => sum + p.Y, 0) / points.length
    };

    const result = [];

    let originPos = points[0];
    let minDist = getDistanceXY(originPos, avgPos);
    points.forEach(p => {
        const d = getDistanceXY(p, avgPos);
        if (d < minDist) {
            minDist = d;
            originPos = p;
        }
    })

    result.push(originPos);

    const remaining = points.filter(p => !result.includes(p));

    while (remaining.length > 0) {
        let bestPoint = null;
        let minDist = Infinity;
        let isCloserToStart = true;

        for (let p of remaining) {
            const distStart = getDistanceXY(p, result[0]);
            const distEnd = getDistanceXY(p, result[result.length - 1]);

            if (distStart < minDist || distEnd < minDist) {
                isCloserToStart = distStart < distEnd;
                minDist = Math.min(distStart, distEnd);
                bestPoint = p;
            }
        }

        if (bestPoint) {
            if (isCloserToStart) {
                result.unshift(bestPoint);
            } else {
                result.push(bestPoint);
            }
            const index = remaining.indexOf(bestPoint);
            if (index > -1) remaining.splice(index, 1);
        } else {
            // Should not happen if remaining has items, but break to prevent infinite loops
            break;
        }
    }

    return result;
}


function getDistanceLatLng(p1, p2) {
    return Math.hypot(p1.lat - p2.lat, p1.lng - p2.lng);
}



function getDistanceXY(p1, p2) {
    return Math.hypot(p1.X - p2.X, p1.Y - p2.Y);
}

export function findClosestPathXY(startRef, endRef, paths) {
    if (!paths || paths.length === 0) return undefined;

    let closestPath = null;
    let closestPathIndex = null;
    let closestPoint = null;
    let minDistance = Infinity;
    let isCloserToStart = true;

    for (let i = 0; i < paths.length; ++i) {
        const path = paths[i];
        let pathMinDist = Infinity;
        let closestPathPoint = null;
        let isCloserToStartOnThisPath = true;

        const startPoint = calculateClosestPointOnPathXY(path, startRef);
        const endPoint = calculateClosestPointOnPathXY(path, endRef);
        const startDist = getDistanceXY(startRef, startPoint);
        const endDist = getDistanceXY(endRef, endPoint);

        if (startDist < endDist) {
            pathMinDist = startDist;
            closestPathPoint = startPoint;
            isCloserToStartOnThisPath = true;
        } else {
            pathMinDist = endDist;
            closestPathPoint = endPoint;
            isCloserToStartOnThisPath = false;
        }

        if (pathMinDist < minDistance) {
            minDistance = pathMinDist;
            closestPath = path;
            closestPathIndex = i;
            closestPoint = closestPathPoint;
            isCloserToStart = isCloserToStartOnThisPath;
        }
    }

    return {path: closestPath, pathIndex: closestPathIndex, point: closestPoint, isCloserToStart: isCloserToStart};
}



// export function calculateServicePathDirectionXY(referenceXY, pathsArrayXY, snappingDistance) {
//     let closestPaths = [];
//     let startRef = referenceXY;
//     let endRef = referenceXY;
//     let pathsCopy = [...pathsArrayXY];
//
//     while (pathsCopy.length > 0 && closestPaths.length < 2) {
//         const closestPath = findClosestPathXY(startRef, endRef, pathsCopy);
//         closestPaths.push(closestPath);
//         const index = pathsCopy.indexOf(closestPath.path);
//         if (index !== -1) {
//             pathsCopy.splice(index, 1);
//         }
//     }
//
//     if (closestPaths.length === 2) {
//         let result = [];
//         closestPaths.forEach(path => {
//             const p = path.point;
//             // const a = path.path[0];
//             // const b = path.path[path.path.length - 1];
//             // if (distXY(a, b) < 3 * snappingDistance || (distXY(a, p) > snappingDistance && distXY(b, p) > snappingDistance)) {
//                 result.push(p);
//             // }
//         });
//         if (result.length === 2) {
//             return result;
//         }
//     }
//     return undefined;
// }


// export function generateServicePathXY(referenceXY, pathsXY, snappingDistance) {
//     let result = [];
//     let startRef = referenceXY;
//     let endRef = referenceXY;
//     let pathsCopy = [...pathsXY];
//
//     while (pathsCopy.length > 0) {
//         const closestPath = findClosestPathXY(startRef, endRef, pathsCopy);
//         if (closestPath) {
//             if (distXY(closestPath.point, closestPath.path[0]) < snappingDistance) {
//                 closestPath.point = closestPath.path[0];
//             } else if (distXY(closestPath.point, closestPath.path[closestPath.path.length - 1]) < snappingDistance) {
//                 closestPath.point = closestPath.path[closestPath.path.length - 1];
//             } else {
//                 // console.log(TAG, "service path index on path", closestPath.point.indexOnPath);
//                 closestPath.splitPaths = [
//                     [...closestPath.path.slice(0, closestPath.point.indexOnPath + 1), closestPath.point],
//                     [closestPath.point, ...closestPath.path.slice(closestPath.point.indexOnPath + 1)]
//                 ];
//                 // console.log(TAG, "service path split paths", closestPath.splitPaths);
//             }
//
//             if (result.length <= 1) {
//                 result.push(closestPath);
//             } else {
//                 if (closestPath.isCloserToStart) {
//                     result.unshift(closestPath);
//                 } else {
//                     result.push(closestPath);
//                 }
//             }
//
//             const index = pathsCopy.indexOf(closestPath.path);
//             if (index !== -1) {
//                 pathsCopy.splice(index, 1);
//             } else {
//                 console.error("cannot build path");
//                 break;
//             }
//         }
//         startRef = result[0].point;
//         endRef = result[result.length - 1].point;
//     }
//     return result;
// }


export function generateDrivingNetworkXY(referenceXY, pathsXY, geoFencePolygon, workAreaPolygon, offsetDistanceM, snappingDistanceM, splitAngleRad, minDistToServicePathM, pathPull, mapScale) {

    const offsetDistance = offsetDistanceM * mapScale;
    const snappingDistance = snappingDistanceM * mapScale;
    const minDistToServicePath = minDistToServicePathM * mapScale;

    let servicePoints = calculateServicePathPointsXY(workAreaPolygon, referenceXY, pathsXY, offsetDistance, snappingDistance, minDistToServicePath, pathPull);
    console.log(TAG, "service points", servicePoints);

    let extensionPaths = [];
    let resultWateringPaths = [];
    let headlandCuttingPoints = [];

    pathsXY.forEach(path => {
       headlandCuttingPoints.push(path.at(0));
       headlandCuttingPoints.push(path.at(-1));
    });

    let pathsCopy = [...pathsXY];
    while (servicePoints.length >= 2 && servicePoints.at(0).isOnEnd && servicePoints.at(1).isOnEnd) {
        let p = servicePoints.at(0).point;
        let path = findClosestPathXY(p, p, pathsCopy);
        servicePoints.splice(0, 1);
        resultWateringPaths.push(path.path);
        pathsCopy.splice(path.pathIndex, 1);
    }
    while (servicePoints.length >= 2 && servicePoints.at(-1).isOnEnd && servicePoints.at(-2).isOnEnd) {
        let p = servicePoints.at(-1).point;
        let path = findClosestPathXY(p, p, pathsCopy);
        servicePoints.splice(servicePoints.length - 1, 1);
        resultWateringPaths.push(path.path);
        pathsCopy.splice(path.pathIndex, 1);
    }

    if (servicePoints.length >= 2) {
        servicePoints.forEach(p => {
            let path = findClosestPathXY(p.point, p.point, pathsCopy);
            if (path) {
                if (p.isOnEnd) {
                    resultWateringPaths.push(path.path);
                } else {
                    let splitPaths = splitLineAtPointXY(path.path, p.point);
                    resultWateringPaths.push(...splitPaths);
                }
                pathsCopy.splice(path.pathIndex, 1);
            }
        });

        if (!servicePoints.at(0).isOnEnd) {
            let overShootPoint = calculateExtensionStartXY(servicePoints.map(p => p.point), offsetDistance);
            let boundaryPoint = calculateClosestPointOnPathXY(workAreaPolygon, overShootPoint);
            servicePoints.unshift({point: boundaryPoint});
            if (!headlandCuttingPoints.includes(boundaryPoint)) {
                headlandCuttingPoints.push(boundaryPoint);
            }
        }
        if (!servicePoints.at(-1).isOnEnd) {
            let overShootPoint = calculateExtensionEndXY(servicePoints.map(p => p.point), offsetDistance);
            let boundaryPoint = calculateClosestPointOnPathXY(workAreaPolygon, overShootPoint);
            servicePoints.push({point: boundaryPoint});
            if (!headlandCuttingPoints.includes(boundaryPoint)) {
                headlandCuttingPoints.push(boundaryPoint);
            }
        }

        let serviceExtensions = generateExtensionsXY(geoFencePolygon, [servicePoints.map(sp => sp.point)], 0.5 * offsetDistanceM, offsetDistanceM, mapScale)[0];
        if (!isPointInPolygonXY(workAreaPolygon, referenceXY)) {
            serviceExtensions = serviceExtensions.filter(ext => !ext.some(p => distXY(p, referenceXY) < offsetDistance));
        }
        console.log(TAG, "service path extensions", serviceExtensions);
        extensionPaths.push(...serviceExtensions);
    }

    let approachPoint = undefined;
    if (servicePoints.length >= 2) {
        // console.log(TAG, "points", JSON.stringify(servicePoints));
        const existingPoint = findClosestPointOnPathXY(servicePoints.map(p => p.point), referenceXY);
        const serviceSegmentPoint = calculateClosestPointOnPathXY(servicePoints.map(p => p.point), referenceXY);
        console.log(TAG, "service segment point", serviceSegmentPoint);

        if (!isPointInPolygonXY(workAreaPolygon, referenceXY)) {
            approachPoint = existingPoint;
        } else if (distXY(existingPoint, serviceSegmentPoint) < 2000) {
            approachPoint = existingPoint;
        } else {
            console.log(TAG, "service segment point splicing", serviceSegmentPoint.indexOnPath + 1);
            servicePoints.splice(serviceSegmentPoint.indexOnPath + 1, 0, {isOnEnd: false, point: serviceSegmentPoint});
            approachPoint = serviceSegmentPoint;
        }

    } else if (isPointInPolygonXY(geoFencePolygon, referenceXY)) {
        const newHeadlandSegmentPoint = calculateClosestPointOnPathXY(workAreaPolygon, referenceXY);
        const existingHeadlandPoint = findClosestPointXY(headlandCuttingPoints, referenceXY);
        if (distXY(existingHeadlandPoint, referenceXY) > 1.41 * distXY(newHeadlandSegmentPoint, referenceXY)) {
            headlandCuttingPoints.push(newHeadlandSegmentPoint);
            approachPoint = newHeadlandSegmentPoint;
        } else {
            approachPoint = existingHeadlandPoint;
        }
    }

    let headlandPaths = cutPolygonAtPoints(workAreaPolygon, headlandCuttingPoints);

    let splitHeadlandPaths = headlandPaths.map(path => splitPathByAngleXY(path, splitAngleRad));

    return {servicePath: servicePoints.map(p => p.point), extensionPaths: extensionPaths, wateringPaths: resultWateringPaths,
        headlandPaths: splitHeadlandPaths, approachPoint: approachPoint};
}



function calculateServicePathPointsXY(workAreaPolygon, referenceXY, pathsXY, offsetDistance, snappingDistance, minDistToServicePath, pathPull) {
    let work = [];
    let startRef = referenceXY;
    let endRef = referenceXY;
    let pathsCopy = [...pathsXY];

    while (pathsCopy.length > 0) {
        const closestPath = findClosestPathXY(startRef, endRef, pathsCopy);
        if (closestPath) {
            let servicePoint = closestPath.point;
            let isOnEnd = false;
            const p0 = closestPath.path.at(0);
            const p1 = closestPath.path.at(-1);
            const d0 = distXY(servicePoint, p0);
            const d1 = distXY(servicePoint, p1);

            if (work.length === 0 && isPointInPolygonXY(workAreaPolygon, referenceXY)) {
                console.log(TAG, "is service point in work area?", isPointInPolygonXY(workAreaPolygon, servicePoint));
                let centerPoint = getMidpointOfPathXY(closestPath.path);
                let dirToCenter = normalizeXY(subXY(centerPoint, servicePoint));
                servicePoint = addXY(servicePoint, scalarMultXY(dirToCenter, minDistToServicePath));
                servicePoint = calculateClosestPointOnPathXY(closestPath.path, servicePoint);
            }

            let needsToSnap = (d0 < snappingDistance || d1 < snappingDistance);

            if (needsToSnap) {
                isOnEnd = true;
                if (d0 < d1) {
                    servicePoint = p0;
                } else {
                    servicePoint = p1;
                }
            } else {
                if (work.length <= 1) {
            //         let centerPoint = getMidpointOfPathXY(closestPath.path);
            //         let dirToCenter = normalizeXY(subXY(centerPoint, servicePoint));
            //         servicePoint = addXY(servicePoint, scalarMultXY(dirToCenter, minDistToServicePath));
            //         servicePoint = calculateClosestPointOnPathXY(closestPath.path, servicePoint);
                } else if (distXY(referenceXY, servicePoint) > offsetDistance * 1.2) {
                    let centerPoint = getMidpointOfPathXY(closestPath.path);
                    servicePoint = lerpXY(centerPoint, servicePoint, pathPull); // TODO maybe gradually change lerp factor
                    servicePoint = calculateClosestPointOnPathXY(closestPath.path, servicePoint);
                }
            }


            const entry = {isOnEnd: isOnEnd, point: servicePoint};
            if (work.length <= 1) {
                work.push(entry);
            } else {
                if (closestPath.isCloserToStart) {
                    work.unshift(entry);
                } else {
                    work.push(entry);
                }
            }

            const index = pathsCopy.indexOf(closestPath.path);
            if (index !== -1) {
                pathsCopy.splice(index, 1);
            } else {
                console.error("cannot build path");
                break;
            }
            if (work.length > 0) {
                startRef = work.at(0).point;
                endRef = work.at(-1).point;
            }
        } else {
            return work;
        }
    }
    return work;
}



export function generateExtensionsXY(geoFence, pathsArray, extensionLengthM, corridorWidthM, mapScale) {
    const scaledLength = extensionLengthM * mapScale;
    const scaledWidth = corridorWidthM * mapScale;

    let result = [];

    if (scaledWidth === 0) {
        pathsArray.forEach(path => {
            result.push([[path[0], calculateExtensionStartXY(path, scaledLength)], [path.at(-1), calculateExtensionEndXY(path, scaledLength)]]);
        });
    } else {
        pathsArray.forEach(path => {
            let startLen = scaledLength;
            let endLen = scaledLength;

            let startExt = [path.at(0), calculateExtensionStartXY(path, startLen)];
            let endExt = [path.at(-1), calculateExtensionEndXY(path, endLen)];

            while (startLen > 0.2 * scaledLength) {
                startExt[1] = calculateExtensionStartXY(path, startLen);
                let rect = calculateRectangleXY(startExt[0], startExt[1],0, 0, scaledWidth);
                if (rect.every(point => isPointInPolygonXY(geoFence, point))) {
                    break;
                } else {
                    startLen -= mapScale;
                }
            }

            while (endLen > 0.2 * scaledLength) {
                endExt[1] = calculateExtensionEndXY(path, endLen);
                let rect = calculateRectangleXY(endExt[0], endExt[1], 0, 0, scaledWidth);
                if (rect.every(point => isPointInPolygonXY(geoFence, point))) {
                    break;
                } else {
                    endLen -= mapScale;
                }
            }

            result.push([startExt, endExt]);
        });
    }
    return result;
}



// Ramer-Douglas-Peucker Algorithm
export function simplifyCurveXY(points, maxDist) {
    if (!Array.isArray(points) || points.length <= 2) return points;

    function distanceToSegment(point, p1, p2) {
        const dx = p2.X - p1.X;
        const dy = p2.Y - p1.Y;
        const lenSq = dx * dx + dy * dy;

        if (lenSq === 0) return Math.sqrt((point.X - p1.X) ** 2 + (point.Y - p1.Y) ** 2);

        const t = ((point.X - p1.X) * dx + (point.Y - p1.Y) * dy) / lenSq;
        const clampedT = Math.max(0, Math.min(1, t));

        const closestX = p1.X + clampedT * dx;
        const closestY = p1.Y + clampedT * dy;

        return Math.sqrt((point.X - closestX) ** 2 + (point.Y - closestY) ** 2);
    }

    function rdp(points, maxDist) {
        if (points.length <= 2) return points;

        const start = points[0];
        const end = points[points.length - 1];

        let maxDistSq = 0;
        let index = 0;

        for (let i = 1; i < points.length - 1; i++) {
            const d = distanceToSegment(points[i], start, end);
            if (d > Math.sqrt(maxDistSq)) {
                maxDistSq = d * d;
                index = i;
            }
        }

        if (Math.sqrt(maxDistSq) <= maxDist) return [start, end];

        const left = rdp(points.slice(0, index + 1), maxDist);
        const right = rdp(points.slice(index), maxDist);

        return [...left.slice(0, -1), ...right];
    }

    return rdp(points, maxDist);
}



export function subdividePathXY(path, approximateDistance) {
    let result = [];
    for (let i = 0; i < path.length - 1; ++i) {
        const a = path[i];
        const b = path[i + 1];
        const d = getDistanceXY(a, b);
        let num = Math.round(d / approximateDistance);
        const dx = (b.X - a.X) / num;
        const dy = (b.Y - a.Y) / num;
        for (let j = 0; j < num; ++j) {
            result.push({X: a.X + j * dx, Y: a.Y + j * dy});
        }
    }
    result.push(path[path.length - 1]);
    return result;
}


export function isPointInPolygonXY(polygon, pt) {
    const x = pt.X, y = pt.Y;
    let isInside = false;
    const n = polygon.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = polygon[i].X, yi = polygon[i].Y, xj = polygon[j].X, yj = polygon[j].Y;
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        isInside = isInside ^ intersect;
    }
    return isInside;
}




export function findClosestPointOnPathLatLng(path, point) {
    if (!path || !Array.isArray(path) || path.length < 2) {
        if (path.length > 0) return path[0];
        return null;
    }

    let closestPoint = undefined;
    let closestIndex = 0;

    let minDistance = Infinity;

    for (let i = 0; i < path.length - 1; ++i) {
        const p1 = path[i];
        const p2 = path[i + 1];
        const candidatePoint = projectOnSegment(p1, p2, point);
        const dist = getDistanceLatLng(point, candidatePoint);

        if (dist < minDistance) {
            closestPoint = candidatePoint;
            closestIndex = i;
            minDistance = dist;
        }
    }

    closestPoint.globalT = (closestIndex + closestPoint.t) / (path.length - 1);

    // console.log(TAG, "closestIndex", closestIndex, "t", closestPoint.t);

    return closestPoint;
}



function projectOnSegment(a, b, p) {
    const dlat = b.lat - a.lat;
    const dlon = b.lng - a.lng;

    const tNum = dlat * (p.lat - a.lat) + dlon * (p.lng - a.lng);
    const distSqSegment = dlat * dlat + dlon * dlon;

    if (distSqSegment === 0) {
        return a;
    }

    let t = tNum / distSqSegment;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;

    const closestLat = a.lat + dlat * t;
    const closestLng = a.lng + dlon * t;
    return { lat: closestLat, lng: closestLng, t: t };
}




export function calculateClosestPointOnPathXY(path, point) {
    if (!path || !Array.isArray(path) || path.length < 2) {
        if (path.length > 0) return path[0];
        return null;
    }

    let closestPoint = undefined;
    let closestIndex = 0;

    let minDistance = Infinity;

    for (let i = 0; i < path.length - 1; ++i) {
        const p1 = path[i];
        const p2 = path[i + 1];
        const candidatePoint = projectOnSegmentXY(p1, p2, point);
        const dist = getDistanceXY(point, candidatePoint);

        if (dist < minDistance) {
            closestPoint = candidatePoint;
            closestIndex = i;
            minDistance = dist;
        }
    }

    closestPoint.globalT = (closestIndex + closestPoint.t) / (path.length - 1);
    closestPoint.extendedT = closestIndex + closestPoint.t;
    closestPoint.indexOnPath = closestIndex;

    // console.log(TAG, "closestIndex", closestIndex, "t", closestPoint.t);

    return closestPoint;
}


export function findClosestPointOnPathXY(path, point) {
    if (!path || !Array.isArray(path) || path.length < 2) {
        if (path.length > 0) return path[0];
        return undefined;
    }

    let closestPoint = undefined;
    let minDistance = Infinity;

    for (let i = 0; i < path.length; ++i) {
        const candidatePoint = path[i];
        const dist = getDistanceXY(point, candidatePoint);

        if (dist < minDistance) {
            closestPoint = candidatePoint;
            minDistance = dist;
        }
    }
    return closestPoint;
}

export function findClosestPointIndexOnPathXY(path, point) {
    let closestIndex = undefined;
    let minDistance = Infinity;

    for (let i = 0; i < path.length; ++i) {
        const dist = getDistanceXY(point, path[i]);
        if (dist < minDistance) {
            closestIndex = i;
            minDistance = dist;
        }
    }
    return closestIndex;
}


export function findClosestEndpointXY(pathsArray, point) {
    if (!pathsArray || !Array.isArray(pathsArray)) {
        return undefined;
    }

    let closestPoint = undefined;
    let minDistance = Infinity;

    for (const item of pathsArray) {
        for (let index of [0, item.length - 1]) {
            const candidatePoint = item[index];
            const dist = getDistanceXY(point, candidatePoint);

            if (dist < minDistance) {
                closestPoint = candidatePoint;
                minDistance = dist;
            }
        }
    }
    return closestPoint;
}



function projectOnSegmentXY(a, b, p) {
    const dx = b.X - a.X;
    const dy = b.Y - a.Y;

    const tNum = dx * (p.X - a.X) + dy * (p.Y - a.Y);
    const distSqSegment = dx * dx + dy * dy;

    if (distSqSegment === 0) {
        return {...a, t: 0};
    }

    let t = tNum / distSqSegment;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;

    const closestX = a.X + dx * t;
    const closestY = a.Y + dy * t;
    return { X: closestX, Y: closestY, t: t };
}



export function subXY(a, b) { return {X: a.X - b.X, Y: a.Y - b.Y}; }
export function addXY(a, b) { return {X: a.X + b.X, Y: a.Y + b.Y}; }
export function scalarMultXY(a, s) { return {X: a.X * s, Y: a.Y * s}; }
export function negateXY(a) { return {X: -a.X, Y: -a.Y}; }
export function norm2XY(a) { return a.X * a.X + a.Y * a.Y; }
export function normXY(a) { return Math.sqrt(a.X * a.X + a.Y * a.Y); }
export function normalizeXY(a) { const n = normXY(a); return {X: a.X / n , Y: a.Y / n}}
export function dotXY(a, b) { return a.X * b.X + a.Y * b.Y; }
export function crossXY(a, b) { return a.X * b.Y - a.Y * b.X; } // 2d cross product, signed area of parallelogram
export function lerpXY(a, b, t) { return {X: (1 - t) * a.X + t * b.X, Y: (1 - t) * a.Y + t * b.Y }; }
export function distXY(a, b) { return Math.hypot(b.X - a.X, b.Y - a.Y); }

function orthogonalXY(a) { // noinspection JSSuspiciousNameCombination
    return {X: -a.Y, Y: a.X};
}


export function cutPolygonAtPoints(polygon, points) {
    console.log(TAG, "headland cutting polygon", polygon);
    console.log(TAG, "headland cutting points", points.sort((a, b) => a.X - b.X));

    let pointsOnPolygon = points.map(p => calculateClosestPointOnPathXY(polygon, p));
    pointsOnPolygon.sort((a, b) => a.extendedT - b.extendedT);

    let result = [];
    for (let i = 0; i < pointsOnPolygon.length; ++i) {
        const p1 = pointsOnPolygon[i];
        const p2 = pointsOnPolygon[(i+1) % pointsOnPolygon.length];
        if (p1.indexOnPath === p2.indexOnPath) {
            result.push([p1, p2]);
        } else if (p1.indexOnPath < p2.indexOnPath) {
            result.push([p1, ...polygon.slice(p1.indexOnPath + 1, p2.indexOnPath + 1), p2]);
        } else {
            result.push([p1, ...polygon.slice(p1.indexOnPath + 1), ...polygon.slice(1, p2.indexOnPath +1), p2]);
        }
    }

    return result;
}


function angleBetweenXY(a, b, c) {
    const v1 = normalizeXY(subXY(b, a));
    const v2 = normalizeXY(subXY(c, b));
    return Math.acos(dotXY(v1, v2));
}

export function splitPolyLinesByAngleXY(paths, maxAngleRad, extensionLength) {
    let result = [];
    for (const path of paths) {
        if (path.length === 2) {
            result.push(path);
        } else {
            const subpaths = [];
            let startIdx = 0;
            for (let i = 0; i < path.length - 2; ++i) {
                const angle = angleBetweenXY(path[i], path[i + 1], path[i + 2]);
                if (angle > maxAngleRad) {
                    let newPath = path.slice(startIdx, i + 2);
                    if (startIdx > 0) {
                        let startExtPoint = calculateExtensionStartXY(newPath, extensionLength);
                        subpaths.push([startExtPoint, newPath[0]]);
                    }
                    subpaths.push(newPath);
                    let endExtPoint = calculateExtensionEndXY(newPath, extensionLength);
                    subpaths.push([endExtPoint, newPath[newPath.length - 1]]);
                    startIdx = i + 1;
                }
            }
            if (startIdx < path.length - 1) {
                let newPath = path.slice(startIdx);
                if (startIdx > 0) {
                    let startExtPoint = calculateExtensionStartXY(newPath, extensionLength);
                    subpaths.push([startExtPoint, newPath[0]]);
                }
                subpaths.push(newPath);
            }
            result.push(...subpaths);
        }
    }
    return result;
}



function splitLineAtPointXY(line, point) {
    let p = calculateClosestPointOnPathXY(line, point);
    return [
        [...line.slice(0, p.indexOnPath + 1), p],
        [p, ...line.slice(p.indexOnPath + 1)]
    ];
}


function splitPathByAngleXY(path, maxAngleRad) {
    let result = [];

    if (path.length === 2) {
        result.push(path);
    } else {
        const subpaths = [];
        let startIdx = 0;
        for (let i = 0; i < path.length - 2; ++i) {
            const angle = angleBetweenXY(path[i], path[i + 1], path[i + 2]);
            if (angle > maxAngleRad) {
                let newPath = path.slice(startIdx, i + 2);
                // if (startIdx > 0) {
                //     let startExtPoint = calculateExtensionStartXY(newPath, extensionLength);
                //     subpaths.push([startExtPoint, newPath[0]]);
                // }
                subpaths.push(newPath);
                // let endExtPoint = calculateExtensionEndXY(newPath, extensionLength);
                // subpaths.push([endExtPoint, newPath[newPath.length - 1]]);
                startIdx = i + 1;
            }
        }
        if (startIdx < path.length - 1) {
            let newPath = path.slice(startIdx);
            // if (startIdx > 0) {
            //     let startExtPoint = calculateExtensionStartXY(newPath, extensionLength);
            //     subpaths.push([startExtPoint, newPath[0]]);
            // }
            subpaths.push(newPath);
        }
        result.push(...subpaths);
    }
    return result;
}


export function generateGraphXY(paths, nodeSnappingDist) {
    let intPaths = paths.map(path => ({type: path.type, points: path.points.map(p => ({X: Math.floor(p.X), Y: Math.floor(p.Y)}))}));

    // console.log(TAG, "paths", paths);
    // console.log(TAG, "int paths", intPaths);

    let nodes = [];

    function insertPathPoints(path) {
        for (let idx of [0, path.points.length - 1]) {
            let p = path.points[idx];
            if (nodes.length === 0) {
                nodes.push(p);
            } else {
                const closestPoint = findClosestPointXY(nodes, p);
                if (distXY(p, closestPoint) <= nodeSnappingDist) {
                    // console.log(TAG, "snapping point", p, "at idx", idx, "to existing node", closestPoint);
                    path.points[idx] = closestPoint;
                } else {
                    nodes.push(p);
                }
            }
        }
    }

    intPaths.filter(path => path.type === pathTypeWatering).forEach(path => insertPathPoints(path));
    intPaths.filter(path => path.type !== pathTypeWatering).forEach(path => insertPathPoints(path));

    let edges = [];
    let edgeTypes = [];
    let edgePaths = [];
    let edgePathLengths = [];
    intPaths.forEach((intPath, index) => {
        let n0 = findClosestPointIndexXY(nodes, intPath.points.at(0));
        let n1 = findClosestPointIndexXY(nodes, intPath.points.at(-1), n0);
        if (n0 >= 0 && n1 >= 0 && n0 !== n1 && !edges.some(e => (e[0] === n0 && e[1] === n1)) && !edges.some(e => (e[0] === n1 && e[1] === n0))) {
            edges.push([n0, n1]);
            edgeTypes.push(intPath.type);
            edgePaths.push(intPath.points);
            edgePathLengths.push(getLinearDistanceLengthXY(intPath.points));
            // console.log(TAG, "path", index, "adding edge", n0, n1);
        } else {
            console.warn(TAG, "path", index, intPath, "no edge", n0, n1);
            let idx1 = edges.findIndex(e => e[0] === n0 && e[1] === n1);
            let idx2 = edges.findIndex(e => e[0] === n1 && e[1] === n0);
            console.log(TAG, "edge index", idx1, idx2, edges[idx1], edges[idx2]);
        }
    })
    // edges.sort((a, b) => (a[0] - b[0]) * nodes.length + a[1] - b[1]);


    return {nodes: nodes, edges: edges, edgeTypes: edgeTypes, paths: edgePaths, pathLengths: edgePathLengths};
}


export function cleanupGraph(graph) {
    let headlandEdgesToWateringEdges = [];
    let headlandExtensionsToWateringExtensions = [];

    for (let i = 0; i < graph.edgeTypes.length; ++i) {
        const type = graph.edgeTypes[i];
        if (type === pathTypeHeadland) {
            const edge = graph.edges[i];
            const connectedTypes = getEdgeTypesAtNode(graph, edge[0]).union(getEdgeTypesAtNode(graph, edge[1]));
            if (!connectedTypes.has(pathTypeWatering)) {
                headlandEdgesToWateringEdges.push(i);
                if (connectedTypes.has(pathTypeHeadlandExtension)) {
                    const edgeDirection = normalizeXY(subXY(graph.nodes[edge[1]], graph.nodes[edge[0]]));
                    for (let i of [0, 1]) {
                        let extensionEdgeIndices = getEdgesByTypeAtNode(graph, edge[i], pathTypeHeadlandExtension);
                        extensionEdgeIndices.forEach(ei => {
                            const extensionEdge = graph.edges[ei];
                            console.log("comparing edges ", edge, extensionEdge);
                            const extensionDirection = normalizeXY(subXY(graph.nodes[extensionEdge[1]], graph.nodes[extensionEdge[0]]));
                            let dot = 0;
                            if (edge[0] === extensionEdge[0]) {
                                // console.log("equal front front");
                                dot = dotXY(edgeDirection, extensionDirection);
                            } else if (edge[0] === extensionEdge[1]) {
                                // console.log("equal front back");
                                dot = dotXY(edgeDirection, negateXY(extensionDirection));
                            } else if (edge[1] === extensionEdge[0]) {
                                // console.log("equal back front");
                                dot = dotXY(edgeDirection, negateXY(extensionDirection));
                            } else if (edge[1] === extensionEdge[1]) {
                                // console.log("equal back back");
                                dot = dotXY(edgeDirection, extensionDirection);
                            }
                            // console.log("DOT: ", dot);
                            if (dot < -0.5) {
                                headlandExtensionsToWateringExtensions.push(ei);
                            }
                        });
                    }
                }
            }
        }
    }
    headlandEdgesToWateringEdges.forEach(i => {
        graph.edgeTypes[i] = pathTypeWatering;
    });
    headlandExtensionsToWateringExtensions.forEach(i => {
        graph.edgeTypes[i] = pathTypeWateringExtension;
    });
}


export function findClosestNodeIdXY(graph, posXY) {
    let result = 0;
    let dist = getDistanceXY(posXY, graph.nodes[result]);

    for (let i = 0; i < graph.nodes.length; ++i) {
        const d = getDistanceXY(posXY, graph.nodes[i]);
        if (d < dist) {
            result = i;
            dist = d;
        }
    }
    return result;
}


function findClosestPointXY(pointList, point) {
    let result = undefined;
    let dist = Infinity;
    for (const p of pointList) {
        let d = distXY(point, p);
        if (d < dist) {
            dist = d;
            result = p;
        }
    }
    return result;
}

function findClosestPointIndexXY(pointList, point, excludeIndex = -1) {
    let result = -1;
    let dist = Infinity;
    for (let i = 0; i < pointList.length; ++i) {
        let p = pointList[i];
        if (i !== excludeIndex) {
            const d = distXY(point, p);
            if (d < dist) {
                dist = d;
                result = i;
            }
        }
    }
    return result;
}


// Kruskal
// export function generateSpanningTree(graphLatLng) {
//
//     const weights = graphLatLng.paths.map(path => getPathLengthLatLng(path));
//     const edges = graphLatLng.edges.map((edge, index) => ({
//         source: edge[0],
//         target: edge[1],
//         weight: weights[index],
//         originalIndex: index
//     }));
//
//     edges.sort((a, b) => a.weight - b.weight);
//
//     const parent = Array.from({ length: graphLatLng.nodes.length }, (_, i) => i);
//     const rank = new Array(graphLatLng.nodes.length).fill(0);
//
//     function find(u) {
//         if (parent[u] !== u) parent[u] = find(parent[u]);
//         return parent[u];
//     }
//
//     function union(u, v) {
//         const rootU = find(u);
//         const rootV = find(v);
//
//         if (rootU === rootV) return false;
//
//         if (rank[rootU] > rank[rootV]) {
//             parent[rootV] = rootU;
//         } else if (rank[rootU] < rank[rootV]) {
//             parent[rootU] = rootV;
//         } else {
//             parent[rootV] = rootU;
//             rank[rootU]++;
//         }
//         return true;
//     }
//
//     const mstEdges = [];
//     for (const edge of edges) {
//         if (union(edge.source, edge.target)) {
//             mstEdges.push(edge);
//             if (mstEdges.length === graphLatLng.nodes.length - 1) break;
//         }
//     }
//
//     return mstEdges.map(e => e.originalIndex);
//
// }

export function getLinearDistanceLengthXY(pathXY) {
    let result = 0;
    for (let i = 0; i < pathXY.length - 1; ++i) {
        result += getDistanceXY(pathXY[i], pathXY[i + 1]);
    }
    return result;
}

export function getPathLengthXY(graph, pathXY) {
    let result = 0;
    for (let i = 0; i < pathXY.length - 1; ++i) {
        const n0 = pathXY[i];
        const n1 = pathXY[i + 1];
        const edge = findEdgeIndex(graph, n0, n1);
        result += graph.pathLengths[edge];
    }
    return result;
}

export function getPathLengthLatLng(pathLatLng) {
    let result = 0;
    for (let i = 0; i < pathLatLng.length - 1; ++i) {
        result += getDistanceLatLng(pathLatLng[i], pathLatLng[i + 1]);
    }
    return result;
}


export function getRouteOnGraph(graphLatLng, startIdx, endIdx) {

    const adjacencyList = new Map();

    for (let i = 0; i < graphLatLng.nodes.length; i++) {
        adjacencyList.set(i, []);
    }

    for (const edge of graphLatLng.edges) {
        const u = edge[0];
        const v = edge[1];
        adjacencyList.get(u).push(v);
        adjacencyList.get(v).push(u);
    }

    const queue = [startIdx];
    const visited = new Set([startIdx]);

    const parentMap = new Map();

    while (queue.length > 0) {
        const current = queue.shift();

        if (current === endIdx) {
            break;
        }

        const neighbors = adjacencyList.get(current);

        for (const neighbor of neighbors) {
            if (!visited.has(neighbor)) {
                visited.add(neighbor);
                parentMap.set(neighbor, current);
                queue.push(neighbor);
            }
        }
    }

    if (!visited.has(endIdx)) {
        console.log(TAG, "end not found");
        return null;
    }

    const path = [];
    let curr = endIdx;

    while (curr !== startIdx) {
        path.push(curr);
        curr = parentMap.get(curr);
    }
    path.push(startIdx);

    return path.reverse();

}



export function findShortestPathWeighted(graphLatLng, startNodeIndex, endNodeIndex) {
    const adjacencyMap = new Map();
    for (let i = 0; i < graphLatLng.nodes.length; i++) {
        adjacencyMap.set(i, []);
    }

    graphLatLng.edges.forEach((edge, index) => {
        const u = edge[0];
        const v = edge[1];
        let w = graphLatLng.pathLengths[index];

        // const nu = currentRoute.findIndex(value => value === u);
        // const nv = currentRoute.findIndex(value => value === v);
        // if (nu >= 0 && nv >= 0 && Math.abs(nu - nv) === 1) {
        //     w *= 4;
        // }
        adjacencyMap.get(u).push({ node: v, weight: w });
        adjacencyMap.get(v).push({ node: u, weight: w });
    });

    // 2. Dijkstra Setup
    // distances map: { nodeIndex: distanceFromStart }
    const distances = new Map();
    const visited = new Set();
    const parentMap = new Map();

    // Initialize distances to infinity, except start node
    for (let i = 0; i < graphLatLng.nodes.length; i++) {
        distances.set(i, Infinity);
    }
    distances.set(startNodeIndex, 0);

    // Simple priority queue (using array sort for small graphs)
    // We store [currentDistance, nodeIndex]
    let queue = [[0, startNodeIndex]];

    while (queue.length > 0) {
        // Sort to find the node with the smallest distance
        queue.sort((a, b) => a[0] - b[0]);

        const [currentDist, u] = queue.shift();

        if (visited.has(u)) continue;
        visited.add(u);

        if (u === endNodeIndex) break;

        const neighbors = adjacencyMap.get(u);
        for (const neighbor of neighbors) {
            const v = neighbor.node;
            const weight = neighbor.weight;

            if (!visited.has(v)) {
                const newDist = currentDist + weight;
                if (newDist < distances.get(v)) {
                    distances.set(v, newDist);
                    parentMap.set(v, u);
                    queue.push([newDist, v]);
                }
            }
        }
    }

    // 3. Reconstruct Path
    if (distances.get(endNodeIndex) === Infinity) return null;

    const path = [];
    let curr = endNodeIndex;
    while (curr !== startNodeIndex) {
        path.push(curr);
        curr = parentMap.get(curr);
    }
    path.push(startNodeIndex);
    return path.reverse();
}



export function calculateRectangleXY(a, b, offsetA, offsetB, width) {

    if (distXY(a, b) < offsetA + offsetB) return undefined;

    const ab = subXY(b, a);
    const abNorm = normalizeXY(ab);

    const c = addXY(a, scalarMultXY(abNorm, offsetA));
    const d = addXY(b, scalarMultXY(abNorm, -offsetB));

    const abOrthNorm = normalizeXY(orthogonalXY(ab));
    const e = addXY(c, scalarMultXY(abOrthNorm, width * 0.5));
    const f = addXY(c, negateXY(scalarMultXY(abOrthNorm, width * 0.5)));
    const g = addXY(d, negateXY(scalarMultXY(abOrthNorm, width * 0.5)));
    const h = addXY(d, scalarMultXY(abOrthNorm, width * 0.5));

    return [e, f, g, h];

}



function getEdgeTypesAtNode(graph, nodeId) {
    let result = new Set();
    let edgeIndices = [];
    graph.edges.forEach((e, i) => {
        if (e[0] === nodeId || e[1] === nodeId) {
            edgeIndices.push(i);
        }
    });
    edgeIndices.forEach(idx => {
        result.add(graph.edgeTypes[idx]);
    })
    return result;
}


function getEdgesByTypeAtNode(graph, nodeId, edgeType) {
    let result = [];
    graph.edges.forEach((e, i) => {
        if (e[0] === nodeId || e[1] === nodeId) {
            if (graph.edgeTypes[i] === edgeType) {
                result.push(i);
            }
        }
    });
    return result;
}


export function getMidpointOfPathXY(path) {
    return getPointAlongOfPathXY(path, 0.5);
}


export function getPointAlongOfPathXY(path, globalT) {
    if (path.length <= 2) {
        return lerpXY(path.at(0), path.at(-1), globalT);
    } else {
        const pathLength = getLinearDistanceLengthXY(path);
        let idx = 1;
        while (getLinearDistanceLengthXY(path.slice(0, idx + 1)) < globalT * pathLength) {
            ++idx;
        }
        const len0 = (1.0 - globalT) * pathLength - getLinearDistanceLengthXY(path.slice(0, idx));
        const len1 = getLinearDistanceLengthXY(path.slice(0, idx + 1)) - globalT * pathLength;

        const lerp = len0 / (len0 + len1);
        return lerpXY(path[idx - 1], path[idx], lerp);
    }
}


export function getGlobalTOnPathXY(path, point) {
    if (path.length < 2) return 0;

    const totalLength = path.reduce((acc, p, i) => i === 0 ? 0 : acc + distXY(p, path[i - 1]), 0);
    if (totalLength === 0) return 0;

    let accumulatedLength = 0;

    for (let i = 0; i < path.length - 1; i++) {
        const p1 = path[i];
        const p2 = path[i + 1];
        const segmentLength = distXY(p1, p2);
        const distToTarget = distXY(p1, point);

        if (distToTarget <= segmentLength) {
            return (accumulatedLength + distToTarget) / totalLength;
        }
        accumulatedLength += segmentLength;
    }
    return 1;
}


export function getContactPointForCircleXY(intersection, vectorSelf, vectorNext, radius) {

    const vs = normalizeXY(vectorSelf);
    const vn = normalizeXY(vectorNext);

    const dot = dotXY(vn, vs);
    if (dot < -0.95) {
        let p = addXY(intersection, scalarMultXY(vs, radius / 10));
        p.isStraight = true;
        return p;
    }
    const angle = Math.acos(dot);
    const halfAngle = angle / 2;
    const distance = radius * Math.cos(halfAngle) / Math.sin(halfAngle);

    let p = addXY(intersection, scalarMultXY(vs, distance));
    p.isStraight = false;
    return p;
}


export function getPointsOnCircleXY(intersection, vectorSelf, vectorNext, pointPrev, pointNext, radius) {

    const vs = normalizeXY(vectorSelf);
    const vn = normalizeXY(vectorNext);

    const dot = dotXY(vn, vs);
    if (dot < -0.95) {
        return [];
    }

    const angle = Math.acos(dot);
    const halfAngle = angle / 2;
    const bisector = normalizeXY(addXY(vs, vn));
    const circleCenter = addXY(intersection, scalarMultXY(bisector, radius / Math.sin(halfAngle)));

    const v50 = normalizeXY(subXY(intersection, circleCenter));
    const p50 = addXY(circleCenter, scalarMultXY(v50, radius));
    p50.isStraight = false;

    const v25 = normalizeXY(subXY(lerpXY(pointPrev, p50, 0.5), circleCenter));
    const p25 = addXY(circleCenter, scalarMultXY(v25, radius));
    p25.isStraight = false;

    const v75 = normalizeXY(subXY(lerpXY(pointNext, p50, 0.5), circleCenter));
    const p75 = addXY(circleCenter, scalarMultXY(v75, radius));
    p75.isStraight = false;

    return [p25, p50, p75];
}


export function findEdgeIndex(graph, nodeIdx1, nodeIdx2) {
    return graph.edges.findIndex(e => (e[0] === nodeIdx1 && e[1] === nodeIdx2) || (e[0] === nodeIdx2 && e[1] === nodeIdx1));
}

export function getTwoPathPointsFromSrcNodeXY(graph, srcNodeId, dstNodeId, minDistance) {
    const edgeId = findEdgeIndex(graph, srcNodeId, dstNodeId);
    const path = graph.paths[edgeId];

    if (edgeId >= 0) {
        const srcNodePos = graph.nodes[srcNodeId];
        if (distXY(srcNodePos, path.at(0)) < distXY(srcNodePos, path.at(-1))) {
            let idx = 1;
            while (distXY(path.at(0), path.at(idx)) < minDistance && idx < path.length - 1) {
                ++idx;
            }
            return [path.at(0), path.at(idx)];
        } else {
            let idx = -2;
            while (distXY(path.at(-1), path.at(idx)) < minDistance && idx > -(path.length)) {
                --idx;
            }
            return [path.at(-1), path.at(idx)];
        }
    }
    return undefined;
}


export function mergePathsXY(polylines) {
    const merged = [];
    const used = new Set();

    const equals = (a, b) => a.X === b.X && a.Y === b.Y;

    for (let i = 0; i < polylines.length; ++i) {
        if (used.has(i)) continue;

        let current = polylines[i];

        if (current.length < 2) {
            merged.push(current);
            used.add(i);
            continue;
        }

        for (let j = 0; j < polylines.length; j++) {
            if (i === j || used.has(j)) continue;

            const other = polylines[j];

            if (equals(current[0], other[other.length - 1])) {
                current = [other, current].flat();
                used.add(j);
            } else if (equals(current[current.length - 1], other[0])) {
                current = [current, other].flat();
                used.add(j);
            } else if (equals(current[0], other[0])) {
                current = [other.slice(1), current].flat();
                used.add(j);
            } else if (equals(current[current.length - 1], other[other.length - 1])) {
                current = [current, other.slice(1)].flat();
                used.add(j);
            }
        }

        merged.push(current);
        used.add(i);
    }

    return merged;
}


export function truncatePathStartingAtPointXY(pathXY, originPointXY, pointXY) {
    let path = distXY(pathXY.at(0), originPointXY) < distXY(pathXY.at(-1), originPointXY) ? pathXY : pathXY.toReversed();
    let globalT = getGlobalTOnPathXY(path, pointXY);
    let index = 0;
    while (getGlobalTOnPathXY(path, path.at(index)) < globalT) {
        ++index;
    }
    return path.slice(index);
}


export function truncatePathEndingAtPointXY(pathXY, originPointXY, pointXY) {
    let path = distXY(pathXY.at(0), originPointXY) < distXY(pathXY.at(-1), originPointXY) ? pathXY : pathXY.toReversed();
    let globalT = getGlobalTOnPathXY(path, pointXY);
    let index = path.length - 1;
    while (getGlobalTOnPathXY(path, path.at(index)) > globalT) {
        --index;
    }
    return path.slice(0, index + 1);
}


export function truncatePathXY(pathXY, originPointXY, startPointXY, endPointXY) {
    let path = distXY(pathXY.at(0), originPointXY) < distXY(pathXY.at(-1), originPointXY) ? pathXY : pathXY.toReversed();
    let startGlobalT = getGlobalTOnPathXY(path, startPointXY);
    let endGlobalT = getGlobalTOnPathXY(path, endPointXY);
    if (startGlobalT < endGlobalT) {
        let startIndex = 0;
        while (getGlobalTOnPathXY(path, path.at(startIndex)) < startGlobalT) {
            ++startIndex;
        }
        let endIndex = path.length - 1;
        while (getGlobalTOnPathXY(path, path.at(endIndex)) > endGlobalT) {
            --endIndex;
        }
        if (startIndex - 1 < endIndex + 1) {
            return path.slice(startIndex, endIndex + 1);
        }
    }
    return [];
}



export function getPath(graph, startNodeIdx, endNodeIdx) {
    const edgeIdx = findEdgeIndex(graph, startNodeIdx, endNodeIdx);
    const path = graph.paths[edgeIdx];
    if (distXY(graph.nodes[startNodeIdx], path[0]) < distXY(graph.nodes[endNodeIdx], path[0])) {
        return path;
    } else {
        return path.toReversed();
    }
}


export function areArraysEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    } else {
        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
    }
    return true;
}




